import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Download, Plus, TrendingUp, TrendingDown, DollarSign, Clock, AlertCircle, Eye } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const ClientProfitability = () => {
  const { user } = useAuth();
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDetail, setShowClientDetail] = useState(false);
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', description: '', revenue_eur_monthly: '' });

  useEffect(() => {
    loadReport();
  }, [month]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/reports/client-profitability?month=${month}`);
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load profitability report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get(`/reports/client-profitability/export?month=${month}&format=csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `profitability_${month}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      await api.post('/projects', {
        ...newClient,
        revenue_eur_monthly: parseFloat(newClient.revenue_eur_monthly)
      });
      toast.success('Proiect creat cu succes');
      setShowAddClient(false);
      setNewClient({ name: '', description: '', revenue_eur_monthly: '' });
      loadReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  const viewClientDetail = async (clientId) => {
    try {
      const response = await api.get(`/reports/client-profitability/${clientId}?month=${month}`);
      setSelectedClient(response.data);
      setShowClientDetail(true);
    } catch (error) {
      toast.error('Failed to load client details');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      GREEN: 'bg-green-100 text-green-700 border-green-300',
      YELLOW: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      RED: 'bg-red-100 text-red-700 border-red-300'
    };
    return colors[status] || colors.GREEN;
  };

  const getStatusIcon = (status) => {
    return status === 'GREEN' ? <TrendingUp className="h-4 w-4" /> : 
           status === 'YELLOW' ? <AlertCircle className="h-4 w-4" /> : 
           <TrendingDown className="h-4 w-4" />;
  };

  if (loading && !report) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-orange border-t-transparent" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-outfit text-4xl font-bold tracking-tight" data-testid="profitability-title">
              Profitabilitate Proiecte
            </h1>
            <p className="mt-2 text-muted-foreground">Monitorizare profitabilitate pe proiect și evaluare performanță</p>
          </div>
          <div className="flex gap-3">
            {user?.role === 'admin' && (
              <Dialog open={showAddClient} onOpenChange={setShowAddClient}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="add-client-btn">
                    <Plus className="mr-2 h-4 w-4" />
                    Adaugă Proiect
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-outfit text-2xl">Adaugă Proiect Nou</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateClient} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="client_name">Nume Proiect *</Label>
                      <Input
                        id="client_name"
                        value={newClient.name}
                        onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                        required
                        data-testid="client-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Descriere</Label>
                      <Input
                        id="description"
                        value={newClient.description}
                        onChange={(e) => setNewClient({ ...newClient, description: e.target.value })}
                        data-testid="client-website-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="revenue">Revenue EUR/lună *</Label>
                      <Input
                        id="revenue"
                        type="number"
                        step="0.01"
                        value={newClient.revenue_eur_monthly}
                        onChange={(e) => setNewClient({ ...newClient, revenue_eur_monthly: e.target.value })}
                        required
                        data-testid="client-revenue-input"
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowAddClient(false)}>
                        Anulează
                      </Button>
                      <Button type="submit" className="bg-brand-orange hover:bg-brand-orange/90" data-testid="submit-client-btn">
                        Creează Proiect
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
            <Button onClick={handleExportCSV} variant="outline" data-testid="export-csv-btn">
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Month Filter */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Label htmlFor="month">Lună:</Label>
              <Input
                id="month"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-48"
                data-testid="month-filter"
              />
              <Button onClick={loadReport} size="sm" className="bg-brand-orange hover:bg-brand-orange/90">
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards */}
        {report && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <Card data-testid="kpi-revenue">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Total Revenue
                </CardTitle>
                <DollarSign className="h-4 w-4 text-brand-orange" />
              </CardHeader>
              <CardContent>
                <div className="font-outfit text-3xl font-bold">
                  €{report.summary.total_revenue_eur.toLocaleString()}
                </div>
              </CardContent>
            </Card>

            <Card data-testid="kpi-cost">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Total Cost
                </CardTitle>
                <TrendingDown className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="font-outfit text-3xl font-bold">
                  €{report.summary.total_cost_eur.toLocaleString()}
                </div>
              </CardContent>
            </Card>

            <Card data-testid="kpi-profit">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Total Profit
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="font-outfit text-3xl font-bold">
                  €{report.summary.total_profit_eur.toLocaleString()}
                </div>
              </CardContent>
            </Card>

            <Card data-testid="kpi-margin">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Overall Margin
                </CardTitle>
                <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                  report.summary.overall_margin_pct >= 35 ? 'bg-green-100' :
                  report.summary.overall_margin_pct >= 20 ? 'bg-yellow-100' : 'bg-red-100'
                }`}>
                  <span className="text-xs font-bold">
                    {report.summary.overall_margin_pct ? `${Math.round(report.summary.overall_margin_pct)}%` : 'N/A'}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="font-outfit text-3xl font-bold">
                  {report.summary.overall_margin_pct ? `${report.summary.overall_margin_pct.toFixed(1)}%` : 'N/A'}
                </div>
              </CardContent>
            </Card>

            <Card data-testid="kpi-hours">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Total Hours
                </CardTitle>
                <Clock className="h-4 w-4 text-brand-orange" />
              </CardHeader>
              <CardContent>
                <div className="font-outfit text-3xl font-bold">
                  {report.summary.total_hours.toFixed(1)}h
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Data Quality Alerts */}
        {report && report.data_quality_alerts.length > 0 && (
          <Card className="border-yellow-300 bg-yellow-50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                Alerte Calitate Date
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {report.data_quality_alerts.map((alert, idx) => (
                  <li key={idx} className="text-sm text-yellow-800">
                    • {alert.message}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Clients Table */}
        {report && (
          <Card data-testid="clients-table-card">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-xl font-semibold">Proiecte - Profitabilitate {month}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/20 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Proiect</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Hours</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Cost</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Profit</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Margin</th>
                      <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Top Angajat</th>
                      <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.clients.map((client) => (
                      <tr key={client.client_id} className="border-b border-border hover:bg-muted/50" data-testid="client-row">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-sm">{client.client_name}</p>
                            {client.website && <p className="text-xs text-muted-foreground">{client.website}</p>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          €{client.revenue_eur ? client.revenue_eur.toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">{client.total_hours.toFixed(1)}h</td>
                        <td className="px-4 py-3 text-right font-mono text-sm">€{client.cost_total_eur.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm font-semibold">
                          €{client.profit_eur ? client.profit_eur.toFixed(2) : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm font-semibold">
                          {client.margin_pct ? `${client.margin_pct.toFixed(1)}%` : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium uppercase tracking-wide border ${getStatusColor(client.status)}`}>
                            {getStatusIcon(client.status)}
                            {client.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {client.top_employee ? (
                            <div>
                              <p className="font-medium">{client.top_employee.name}</p>
                              <p className="text-xs text-muted-foreground">{client.top_employee.hours.toFixed(1)}h</p>
                            </div>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => viewClientDetail(client.client_id)}
                            data-testid="view-client-detail-btn"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {report.clients.length === 0 && (
                  <div className="py-12 text-center text-muted-foreground">
                    Nu există date pentru luna selectată
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Client Detail Dialog */}
        <Dialog open={showClientDetail} onOpenChange={setShowClientDetail}>
          <DialogContent className="max-w-4xl">
            {selectedClient && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-outfit text-2xl">
                    {selectedClient.client_name} - Detalii Profitabilitate
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-6">
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Revenue</p>
                      <p className="font-outfit text-2xl font-bold">€{selectedClient.revenue_eur?.toLocaleString() || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Cost</p>
                      <p className="font-outfit text-2xl font-bold">€{selectedClient.cost_total_eur.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Profit</p>
                      <p className="font-outfit text-2xl font-bold text-green-600">€{selectedClient.profit_eur?.toFixed(2) || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Margin</p>
                      <p className="font-outfit text-2xl font-bold">{selectedClient.margin_pct ? `${selectedClient.margin_pct.toFixed(1)}%` : 'N/A'}</p>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-lg mb-3">Ore pe Angajat</h3>
                    <table className="w-full text-sm">
                      <thead className="bg-muted/20 border-b border-border">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs uppercase">Angajat</th>
                          <th className="px-3 py-2 text-right text-xs uppercase">Ore</th>
                          <th className="px-3 py-2 text-right text-xs uppercase">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedClient.hours_by_employee.map((emp) => (
                          <tr key={emp.employee_id} className="border-b border-border">
                            <td className="px-3 py-2">{emp.employee_name}</td>
                            <td className="px-3 py-2 text-right font-mono">{emp.hours.toFixed(2)}h</td>
                            <td className="px-3 py-2 text-right font-mono">€{emp.cost_eur.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};